import { useSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { UserWorkspacesContext } from '../../contexts/UserWorkspacesContext'
import { CommonSchema } from '../../schemas'
import { Data, UPDATE_PUBLISHER_WORKSPACE_ROLE, Variables } from './api'
import { userWorkspaceFromDTO } from './mapper'
import { UseUpdatePublisherWorkspaceRole } from './types'

export const useUpdatePublisherWorkspaceRole = (): UseUpdatePublisherWorkspaceRole => {
  const { t } = useTranslation('features/user')
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<CommonSchema>>({})
  const { update } = useContext(UserWorkspacesContext)

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_PUBLISHER_WORKSPACE_ROLE,
    onCompleted: async (data: Data) => {
      const {
        updatePublisherWorkspaceRole: { errors, userWorkspace },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.publisher.edit.error'), { variant: 'error' })
      } else if (userWorkspace) {
        setErrors({})
        enqueueSnackbar(t('form.publisher.edit.success'), {
          variant: 'success',
        })
        update(userWorkspaceFromDTO(userWorkspace))
      }
    },
  })

  const updatePublisherWorkspaceRole = ({ role, userId, workspaceId }: CommonSchema): void => {
    void mutateAsync({
      input: {
        role,
        userId,
        workspaceId: workspaceId.value,
      },
    })
  }

  return {
    errors,
    loading: isPending,
    updatePublisherWorkspaceRole,
  }
}
